import { DownloadOutlined, DownOutlined, PlayCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { BreadcrumbItem } from '@zeus/core/layout/MainLayout';
import Loader from '@zeus/core/Loader';
import { useCollection } from '_/contexts/CollectionContext';
import { useTopHeader } from '_/contexts/LayoutContext';
import { useOrganization } from '_/contexts/OrganizationContext';
import { useProject } from '_/contexts/ProjectContext';
import { useSnapshotContext } from '_/contexts/SnapshotContext';
import { useSourceContext } from '_/contexts/SourceContext';
import { endpoints } from '_/lib/frontend-utils';
import zeusSdk from '_/utils/SDKs';
import ace from 'ace-builds/src-noconflict/ace';
import { Button, Dropdown, Input, Modal, Select, Space, Spin, Table, Tabs, Tooltip, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AceEditor from 'react-ace';

import 'ace-builds/src-noconflict/mode-sql';
import 'ace-builds/src-noconflict/theme-monokai';
import 'ace-builds/src-noconflict/ext-language_tools';

// Configure Ace editor base path for Vite
ace.config.set('basePath', 'https://cdn.jsdelivr.net/npm/ace-builds@1.43.6/src-noconflict');

import styles from './SnapshotDrilldown.module.css';

const { Text, Link: AntLink } = Typography;

const MIN_COLUMN_WIDTH = 50;

interface ResizableTitleProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  width?: number;
  onResize?: (width: number) => void;
}

// Manual drag-to-resize header cell (antd doesn't support resizable columns out of the box,
// and the official react-resizable-based recipe isn't a dependency of this project).
function ResizableTitle({ width, onResize, style, children, ...restProps }: ResizableTitleProps): ReactElement {
  const thRef = useRef<HTMLTableCellElement>(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const [isResizing, setIsResizing] = useState(false);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    startXRef.current = e.clientX;
    startWidthRef.current = thRef.current?.offsetWidth ?? width ?? MIN_COLUMN_WIDTH;
    setIsResizing(true);
  };

  useEffect(() => {
    if (!isResizing) return undefined;

    const handleMouseMove = (e: MouseEvent) => {
      const newWidth = Math.max(MIN_COLUMN_WIDTH, startWidthRef.current + (e.clientX - startXRef.current));
      onResize?.(newWidth);
    };
    const handleMouseUp = () => setIsResizing(false);

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, onResize]);

  if (!width) {
    return (
      <th {...restProps} style={style}>
        {children}
      </th>
    );
  }

  return (
    <th {...restProps} ref={thRef} style={{ ...style, position: 'relative' }}>
      {children}
      <span
        className={isResizing ? `${styles.resizeHandle} ${styles.resizeHandleActive}` : styles.resizeHandle}
        onMouseDown={handleMouseDown}
        onClick={(e) => e.stopPropagation()}
      />
    </th>
  );
}

interface QueryResult {
  [key: string]: any;
}

interface TopNValue {
  name: string;
  value?: string;
  count?: number;
  values?: TopNValue[];
  _children?: TopNValue[];
}

interface ValidationIssue {
  name: string;
  count: number;
}

interface SnapshotData {
  id: string;
  version?: number;
  crawlRunId?: string;
  [key: string]: any;
}

function SnapshotDrilldown(): ReactElement {
  const { selectedOrgSlug, selectedOrganization } = useOrganization();
  const { selectedProjectSlug, selectedProject } = useProject();
  const { selectedCollectionSlug, selectedCollection } = useCollection();
  const { selectedSourceSlug, selectedSource } = useSourceContext();
  const { snapshotId, selectedSnapshot, isLoading: isLoadingSnapshot } = useSnapshotContext();
  const { setBreadcrumbs } = useTopHeader();

  // State
  const [activeTab, setActiveTab] = useState('data');
  const [sqlQuery, setSqlQuery] = useState('');
  const [selectedAsset, setSelectedAsset] = useState('data');
  const [queryResults, setQueryResults] = useState<QueryResult[]>([]);
  const [isQueryLoading, setIsQueryLoading] = useState(false);
  const [rowCount, setRowCount] = useState<number>(0);
  const [tableColumns, setTableColumns] = useState<ColumnsType<any>>([]);
  const [topNData, setTopNData] = useState<TopNValue[]>([]);
  const [validationData, setValidationData] = useState<ValidationIssue[]>([]);
  const [formulaInput, setFormulaInput] = useState('');
  const [compareModalOpen, setCompareModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any>(null);
  const [htmlContent, setHtmlContent] = useState('');

  const editorRef = useRef<any>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const snapshot = selectedSnapshot as SnapshotData | undefined;

  // Reusable cell value renderer (matching footer.hbs formatters)
  const renderCellValue = (key: string, value: any, crawlRunId?: string): React.ReactNode => {
    const debuggerUrl = 'https://debugger.import.io';
    const env = window.location.origin.includes('import.io') ? 'production' : 'demo';

    // Helper to render a single non-array value
    const renderSingleValue = (val: any): React.ReactNode => {
      // Extract element if it's an object with element property
      const actualVal = val && typeof val === 'object' && val.element ? val.element : val;

      if (actualVal == null) return <Text type="secondary">null</Text>;

      // Check for URLs (linkFormatter / imageWithLinkFormatter)
      if (typeof actualVal === 'string' && /^https?:/.test(actualVal)) {
        // Image URLs get thumbnail
        if (/image/i.test(key) || /\.(jpg|gif|png)([?#]|$)/i.test(actualVal)) {
          return (
            <a href={actualVal} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
              <img src={actualVal} alt="" width={50} loading="lazy" style={{ maxHeight: '50px' }} />
            </a>
          );
        }
        // Regular URL link
        return (
          <a
            href={actualVal}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className={styles.cellLink}
          >
            {actualVal}
          </a>
        );
      }

      // Handle numbers (numberFormatter / epochMillisFormatter)
      if (typeof actualVal === 'number') {
        // Check if it looks like a timestamp (between 2018 and 2030)
        if (/timestamp/i.test(key) && actualVal > 1517443200000 && actualVal < 1896134400000) {
          return new Date(actualVal).toLocaleString();
        }
        // Regular number formatting
        if (Number.isInteger(actualVal)) return String(actualVal);
        return actualVal.toFixed(3);
      }

      // Handle booleans
      if (typeof actualVal === 'boolean') {
        return String(actualVal);
      }

      // Handle objects (jsonFormatter)
      if (typeof actualVal === 'object') {
        return <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{JSON.stringify(actualVal, null, 2)}</div>;
      }

      // Default text formatter - return plain string for ellipsis to work
      return String(actualVal);
    };

    // Special handling for _htmlExtraction
    if (key === '_htmlExtraction' && Array.isArray(value) && value.length > 0) {
      const url = value[value.length - 1]?.element || value[value.length - 1];
      return (
        <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className={styles.cellLink}>
          HTML
        </a>
      );
    }

    // Special handling for _screenCapture
    if (key === '_screenCapture' && Array.isArray(value) && value.length > 0) {
      const url = value[value.length - 1]?.element || value[value.length - 1];
      return (
        <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className={styles.cellLink}>
          Image
        </a>
      );
    }

    if (value == null) return <Text type="secondary">null</Text>;

    // Special handling for _input - parse JSON string (jsonStringFormatter)
    if (key === '_input' && typeof value === 'string' && value) {
      try {
        const parsed = JSON.parse(value);
        return <div style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{JSON.stringify(parsed, null, 2)}</div>;
      } catch {
        return <div style={{ fontFamily: 'monospace' }}>{value}</div>;
      }
    }

    // Add debugger links for taskId fields
    if (key.toLowerCase().includes('taskid') && crawlRunId && value) {
      const url = `${debuggerUrl}/?env=${env}&crawlRunId=${crawlRunId}:${value}`;
      return (
        <a href={url} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className={styles.cellLink}>
          {String(value)}
        </a>
      );
    }

    // Handle arrays - render each item with proper formatting
    if (Array.isArray(value)) {
      return (
        <>
          {value.map((item, idx) => (
            <div key={idx} style={{ marginBottom: '4px' }}>
              {renderSingleValue(item)}
            </div>
          ))}
        </>
      );
    }

    return renderSingleValue(value);
  };

  const convertS3HtmlAssetUrl = (htmlUrlOrObject: any): string => {
    const htmlUrl = typeof htmlUrlOrObject === 'string' ? htmlUrlOrObject : htmlUrlOrObject?.element;
    if (!htmlUrl) return htmlUrl;

    const htmlBucket = 'chromium-htmls';
    const htmlRegion = 'us-east-1';

    return htmlUrl.replace(
      `https://${htmlBucket}.s3.${htmlRegion}.amazonaws.com`,
      `https://crawl-assets.${endpoints.rootDomain}/html`,
    );
  };

  const convertS3ScreenCaptureAssetUrl = (screenCapUrlOrObject: any): string => {
    const screenCapUrl = typeof screenCapUrlOrObject === 'string' ? screenCapUrlOrObject : screenCapUrlOrObject?.element;
    if (!screenCapUrl) return screenCapUrl;

    const screenshotBucket = 'chromium-screenshots';
    const screenshotRegion = 'us-east-1';

    return screenCapUrl.replace(
      `https://${screenshotBucket}.s3.${screenshotRegion}.amazonaws.com`,
      `https://crawl-assets.${endpoints.rootDomain}/screenshot`,
    );
  };

  // Set up breadcrumbs
  useEffect(() => {
    const breadcrumbs: BreadcrumbItem[] = [
      { title: 'Organizations', href: '/orgs' },
      { title: selectedOrganization?.name || '', href: `/orgs/${selectedOrgSlug}` },
      { title: 'Projects', href: `/orgs/${selectedOrgSlug}/projects` },
      {
        title: selectedProject?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/home`,
      },
      {
        title: 'Collections',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections`,
      },
      {
        title: selectedCollection?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/home`,
      },
      {
        title: 'Sources',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources`,
      },
      {
        title: selectedSource?.name || '',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/home`,
      },
      {
        title: 'Snapshots',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/snapshots`,
      },
      {
        title: snapshotId || 'Snapshot',
        href: `/orgs/${selectedOrgSlug}/projects/${selectedProjectSlug}/collections/${selectedCollectionSlug}/sources/${selectedSourceSlug}/snapshots/${snapshotId}/home`,
      },
      { title: 'Drilldown' },
    ];

    setBreadcrumbs(breadcrumbs);

    return () => {
      setBreadcrumbs([]);
    };
  }, [
    selectedOrganization?.name,
    selectedOrgSlug,
    selectedProject?.name,
    selectedProjectSlug,
    selectedCollection?.name,
    selectedCollectionSlug,
    selectedSource?.name,
    selectedSourceSlug,
    snapshotId,
    setBreadcrumbs,
  ]);

  // Initialize SQL query and asset selection
  useEffect(() => {
    const defaultQuery = 'SELECT * FROM S3Object s LIMIT 200';
    setSqlQuery(defaultQuery);

    // Set default asset based on snapshot version
    if (snapshot?.version && snapshot.version >= 2) {
      setSelectedAsset('internal-data');
    }
  }, [snapshot?.version]);

  // Fetch Top N data
  useEffect(() => {
    if (snapshotId) {
      fetchTopNData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotId]);

  // Fetch validation data
  useEffect(() => {
    if (snapshot?.validationSummary) {
      const validation = Object.keys(snapshot.validationSummary).map((key) => ({
        name: key,
        count: snapshot.validationSummary[key],
      }));
      setValidationData(validation);
    }
  }, [snapshot?.validationSummary]);

  // CloudWatch link builder
  const getCloudWatchLogsLink = useMemo(() => {
    if (!snapshot?.crawlRunId) return null;

    const isProduction = window.location.origin.includes('import.io');
    const region = 'us-east-2';

    const productionLogGroups = [
      'k8s-cell-production-bn1/bees',
      'k8s-cell-production-bn1/bees-api',
      'k8s-cell-production-hn1/bees',
      'k8s-cell-production-hn1/bees-api',
      'k8s-cell-production-b1/bees-api',
      'k8s-cell-production-b1/bees',
      'k8s-hetzner-blue/bees-api',
      'k8s-hetzner-blue/bees',
    ];

    const demoLogGroups = ['k8s-cell-demo-a1/bees', 'k8s-cell-demo-a1/bees-api'];

    const logGroups = isProduction ? productionLogGroups : demoLogGroups;

    const startTime = snapshot.startedAt ? new Date(snapshot.startedAt) : new Date(Date.now() - 24 * 60 * 60 * 1000);
    const endTime = snapshot.stoppedAt ? new Date(snapshot.stoppedAt) : new Date();

    // Format times as ISO string with CloudWatch encoding (*3a for colon)
    const startTimeStr = startTime.toISOString().replace(/:/g, '*3a');
    const endTimeStr = endTime.toISOString().replace(/:/g, '*3a');

    // Build the Logs Insights query with CloudWatch-specific encoding
    const query = `fields @timestamp, level, msg\n| filter mdc.runId = '${snapshot.crawlRunId}'\n| sort @timestamp desc\n| limit 2000`;

    // CloudWatch uses special encoding: *20=space, *40=@, *2c=comma, *0a=newline, *7c=pipe, *3d=equals, *27=quote, *2f=slash
    const encodedQuery = query
      .replace(/ /g, '*20')
      .replace(/@/g, '*40')
      .replace(/,/g, '*2c')
      .replace(/\n/g, '*0a')
      .replace(/\|/g, '*7c')
      .replace(/=/g, '*3d')
      .replace(/'/g, '*27');

    // Build the source array with CloudWatch encoding
    const sourceArray = logGroups.map((lg) => `'${lg.replace(/\//g, '*2f')}`).join('~');

    // CloudWatch Logs Insights URL format with ISO timestamp format
    const queryDetail = `~(end~'${endTimeStr}~start~'${startTimeStr}~timeType~'ABSOLUTE~tz~'UTC~unit~'seconds~editorString~'${encodedQuery}~source~(~${sourceArray})~lang~'CWLI~logClass~'STANDARD~queryBy~'logGroupName)`;

    return `https://${region}.console.aws.amazon.com/cloudwatch/home?region=${region}#logsV2:logs-insights$3FqueryDetail$3D${queryDetail}`;
  }, [snapshot?.crawlRunId, snapshot?.startedAt, snapshot?.stoppedAt]);

  const fetchTopNData = useCallback(async () => {
    if (!snapshotId) return;

    try {
      // Get S3 URL from backend without redirect
      const s3Url = await zeusSdk.snapshots.getAssetLocation(snapshotId, 'stats.json');

      if (!s3Url) {
        throw new Error('No S3 URL returned from getAssetLocation');
      }

      // Fetch from S3 without credentials to avoid CORS error
      const s3Response = await fetch(s3Url, { credentials: 'omit' });
      if (!s3Response.ok) {
        throw new Error(`S3 fetch failed: ${s3Response.status}`);
      }

      const text = await s3Response.text();
      const stats = JSON.parse(text);
      const topn = JSON.parse(JSON.stringify(stats.topN));
      topn.forEach((x: TopNValue) => {
        if (x.values) {
          x.count = x.values.reduce((acc, v) => acc + (v.count || 0), 0);
          // Copy parent name to children so they can be clickable
          x._children = x.values.map((child) => ({ ...child, name: x.name }));
        }
      });
      setTopNData(topn);
    } catch (error) {
      console.error('Error fetching top N data:', error);
    }
  }, [snapshotId]);

  const executeQuery = async (queryOverride?: string) => {
    if (!snapshotId) return;

    setIsQueryLoading(true);
    setQueryResults([]);

    try {
      let query = (queryOverride || sqlQuery).trim();
      if (!/\blimit\b/i.test(query)) {
        query += ' LIMIT 200';
      }

      const results = await zeusSdk.snapshots.execute({
        snapshotGuid: snapshotId,
        sql: query,
        where: '',
        target: selectedAsset,
      });

      // Add row IDs and convert S3 URLs to workbench proxy URLs
      const resultsWithIds = results.map((row, index) => {
        const newRow = { ...row, _rowId: `row-${index}` };

        // Convert _htmlExtraction URLs
        if (Array.isArray(newRow._htmlExtraction)) {
          newRow._htmlExtraction = newRow._htmlExtraction.map(convertS3HtmlAssetUrl);
        }

        // Convert _screenCapture URLs
        if (Array.isArray(newRow._screenCapture)) {
          newRow._screenCapture = newRow._screenCapture.map(convertS3ScreenCaptureAssetUrl);
        }

        return newRow;
      });

      setQueryResults(resultsWithIds);
      setRowCount(resultsWithIds.length);

      if (resultsWithIds.length > 0) {
        generateColumns(resultsWithIds);
      }
    } catch (error) {
      console.error('Query error:', error);
      message.error('Failed to execute query');
    } finally {
      setIsQueryLoading(false);
    }
  };

  const handleColumnResize = useCallback((key: string, newWidth: number) => {
    setTableColumns((prevColumns) => prevColumns.map((col) => (col.key === key ? { ...col, width: newWidth } : col)));
  }, []);

  const generateColumns = (data: QueryResult[]) => {
    if (data.length === 0) return;

    const crawlRunId = snapshot?.crawlRunId;

    // Get all unique column names from all rows
    const namesSet = new Set<string>();
    for (const obj of data) {
      Object.keys(obj).forEach((key) => namesSet.add(key));
    }

    // Remove _raw column
    namesSet.delete('_raw');

    // Convert to array and sort (_-prefixed columns at the end)
    const columnNames = Array.from(namesSet).sort((a, b) => {
      if (a[0] === '_' && b[0] !== '_') return 1;
      if (b[0] === '_' && a[0] !== '_') return -1;
      return a.localeCompare(b);
    });

    // Calculate header height based on longest column name (~7px per character for vertical text)
    const maxColumnNameLength = Math.max(...columnNames.map((name) => name.length));
    const headerHeight = Math.max(100, Math.min(300, maxColumnNameLength * 7 + 20));

    const cols: ColumnsType<any> = columnNames.map((key) => {
      // Determine width based on column type (matching original Tabulator behavior)
      let width: number | undefined;
      const objWithData = data.find((d) => d[key] != null);
      const sampleValue = objWithData ? objWithData[key] : null;
      const valueType = typeof sampleValue;

      if (key === '_input') {
        width = 150;
      } else if (valueType === 'object') {
        width = 150;
      } else if (valueType === 'string') {
        const hasLongValue = data.some((d) => {
          const val = d[key];
          if (val == null) return false;
          const strVal = Array.isArray(val) ? val.join('') : String(val);
          return strVal.length > 30;
        });
        width = hasLongValue ? 150 : 100;
      } else {
        width = 100;
      }

      return {
        title: key,
        dataIndex: key,
        key: key,
        width: width,
        onHeaderCell: (column: any) => ({
          width: column.width as number,
          onResize: (newWidth: number) => handleColumnResize(key, newWidth),
          style: {
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            whiteSpace: 'nowrap',
            height: `${headerHeight}px`,
            padding: '4px 2px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          },
          title: key, // Show full name on hover
        }),
        sorter: (a, b) => {
          const aVal = a[key];
          const bVal = b[key];

          if (aVal == null && bVal == null) return 0;
          if (aVal == null) return -1;
          if (bVal == null) return 1;

          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return aVal - bVal;
          }

          const aStr = String(aVal);
          const bStr = String(bVal);
          return aStr < bStr ? -1 : aStr > bStr ? 1 : 0;
        },
        render: (value: any) => (
          <div style={{ maxHeight: '100px', overflow: 'auto', wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
            {renderCellValue(key, value, crawlRunId)}
          </div>
        ),
        onCell: (record: any) => {
          const style: React.CSSProperties = {};

          // Highlight validation errors
          if (record._validationErrors && Array.isArray(record._validationErrors)) {
            const prefix = key + '/';
            const hasError = record._validationErrors.some((err: any) => {
              const errStr = typeof err === 'string' ? err : err?.element;
              return errStr && errStr.startsWith(prefix);
            });
            if (hasError) {
              style.backgroundColor = 'orange';
            }
          }

          // Highlight dupes/filtered rows
          if (record._dupe || record._filtered) {
            style.backgroundColor = '#2f4f4f';
            style.color = 'white';
          }

          return { style: style };
        },
      };
    });

    setTableColumns(cols);
  };

  const handleRowClick = (record: QueryResult) => {
    setSelectedRowData(record);

    if (record._htmlExtraction && record._htmlExtraction.length > 0) {
      const urlData = record._htmlExtraction[record._htmlExtraction.length - 1];

      // Handle both string URLs and objects with URL property
      let url: string | null = null;
      if (typeof urlData === 'string') {
        url = urlData;
      } else if (typeof urlData === 'object' && urlData !== null) {
        url = urlData.element || urlData.url || urlData.URL || urlData.uri || null;
      }

      if (url && typeof url === 'string') {
        fetchHtmlContent(url);
      } else {
        console.warn('Invalid HTML extraction URL:', urlData);
        setHtmlContent('NO HTML AVAILABLE');
      }
      setCompareModalOpen(true);
    } else {
      setHtmlContent('NO HTML AVAILABLE');
      setCompareModalOpen(true);
    }
  };

  const fetchHtmlContent = async (url: string) => {
    try {
      // Convert S3 URL to crawl-assets URL
      const htmlBucket = 'chromium-htmls';
      const htmlRegion = 'us-east-1';
      const fetchUrl = url.replace(
        `https://${htmlBucket}.s3.${htmlRegion}.amazonaws.com`,
        `https://crawl-assets.${endpoints.rootDomain}/html`,
      );

      const response = await fetch(fetchUrl);
      const html = await response.text();
      setHtmlContent(html);
    } catch (error) {
      console.error('Error fetching HTML:', error);
      setHtmlContent('ERROR LOADING HTML');
    }
  };

  const downloadData = (format: 'csv' | 'json' | 'xlsx') => {
    if (queryResults.length === 0) {
      message.warning('No data to download');
      return;
    }

    if (format === 'json') {
      const dataStr = JSON.stringify(queryResults, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'data.json';
      link.click();
    } else if (format === 'csv') {
      const headers = Object.keys(queryResults[0]);
      const csvContent = [
        headers.join(','),
        ...queryResults.map((row) => headers.map((h) => JSON.stringify(row[h] ?? '')).join(',')),
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'data.csv';
      link.click();
    } else {
      message.info('XLSX download not yet implemented');
    }
  };

  const handleTopNRowClick = (record: TopNValue) => {
    if (!record.value || !record.name || !record.name.startsWith('data:')) return;

    const col = record.name.replace(/^data:\w+\//, '').split('/')[0];
    const sqlValue = record.value.replace(/'/g, "\\'");
    const whereClause = `WHERE '${sqlValue}' IN ${col} OR ${col} LIKE '${sqlValue}%'`;
    const query = `SELECT * FROM S3Object s ${whereClause} LIMIT 200`;
    setSqlQuery(query);
    setActiveTab('data');
    // Pass query directly to avoid state timing issues
    setTimeout(() => executeQuery(query), 100);
  };

  const handleValidationRowClick = (record: ValidationIssue) => {
    const whereClause = `WHERE '${record.name}' IN _validationErrors`;
    const query = `SELECT * FROM S3Object s ${whereClause} LIMIT 200`;
    setSqlQuery(query);
    setActiveTab('data');
    // Pass query directly to avoid state timing issues
    setTimeout(() => executeQuery(query), 100);
  };

  if (isLoadingSnapshot) {
    return <Loader />;
  }

  const tabItems = [
    {
      key: 'data',
      label: (
        <Space>
          Query Data
          {isQueryLoading && <Spin size="small" />}
        </Space>
      ),
      children: (
        <div className={styles.tabContent}>
          <div className={styles.editorSection}>
            <AceEditor
              ref={editorRef}
              mode="sql"
              theme="monokai"
              value={sqlQuery}
              onChange={setSqlQuery}
              name="sql-editor"
              width="100%"
              height="150px"
              fontSize={14}
              showPrintMargin={false}
              showGutter={true}
              highlightActiveLine={true}
              setOptions={{
                enableBasicAutocompletion: true,
                enableLiveAutocompletion: true,
                enableSnippets: true,
                showLineNumbers: true,
                tabSize: 2,
                useWorker: false,
              }}
            />
          </div>

          <Space className={styles.controls} wrap>
            <Button type="primary" icon={<PlayCircleOutlined />} loading={isQueryLoading} onClick={() => executeQuery()}>
              Run
            </Button>
            <Select value={selectedAsset} onChange={setSelectedAsset} style={{ width: 200 }}>
              {snapshot?.version && snapshot.version >= 2 && <Select.Option value="internal-data">Data - internal</Select.Option>}
              <Select.Option value="data">Data - external</Select.Option>
              <Select.Option value="pages">Pages</Select.Option>
            </Select>
            {getCloudWatchLogsLink && (
              <Tooltip title="View CloudWatch logs for bees">
                <Button
                  type="link"
                  icon={<InfoCircleOutlined />}
                  href={getCloudWatchLogsLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  CloudWatch Logs
                </Button>
              </Tooltip>
            )}
          </Space>

          <div className={styles.rowCount}>
            <Text>
              {rowCount} rows returned <Text type="secondary">(Returning too many rows may crash your browser)</Text>
            </Text>
          </div>

          {queryResults.length > 0 && (
            <>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 12 }}>
                <Space className={styles.downloadControls} wrap style={{ marginBottom: 0 }}>
                  <Input
                    placeholder="Formula to calculate"
                    value={formulaInput}
                    onChange={(e) => setFormulaInput(e.target.value)}
                    style={{ width: 300 }}
                  />
                  <Tooltip
                    title={
                      <div>
                        <a
                          href="https://github.com/handsontable/formula.js/tree/master/test"
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ color: '#69c0ff' }}
                        >
                          Supported functions
                        </a>
                        , plus PARAM('name') to get a source parameter, plus INPUT('name') to get an input parameter,
                        OUTPUT('name') to get output value
                      </div>
                    }
                  >
                    <InfoCircleOutlined />
                  </Tooltip>
                </Space>
                <div style={{ flex: 1 }} />
                <Dropdown
                  menu={{
                    items: [
                      { key: 'csv', label: 'CSV', onClick: () => downloadData('csv') },
                      { key: 'json', label: 'JSON', onClick: () => downloadData('json') },
                      { key: 'xlsx', label: 'XLSX', onClick: () => downloadData('xlsx') },
                    ],
                  }}
                >
                  <Button icon={<DownloadOutlined />}>
                    Download <DownOutlined />
                  </Button>
                </Dropdown>
              </div>

              <div className={styles.tableContainer}>
                <Table
                  columns={tableColumns}
                  dataSource={queryResults}
                  rowKey={(record, index) => record._rowId ?? index}
                  scroll={{ x: 'max-content', y: 'calc(100vh - 450px)' }}
                  tableLayout="fixed"
                  pagination={false}
                  size="small"
                  virtual
                  components={{ header: { cell: ResizableTitle } }}
                  onRow={(record) => ({
                    onClick: () => handleRowClick(record),
                    style: { cursor: 'pointer' },
                  })}
                />
              </div>
            </>
          )}
        </div>
      ),
    },
    {
      key: 'topn',
      label: 'Top Values',
      children: (
        <div className={styles.tabContent}>
          <Table
            columns={[
              {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
                width: 300,
                filterSearch: true,
              },
              {
                title: 'Value',
                dataIndex: 'value',
                key: 'value',
                render: (value: string, record: TopNValue) => {
                  if (!value || !record.name || !record.name.startsWith('data:')) return value;
                  return (
                    <AntLink className={styles.cellLink} onClick={() => handleTopNRowClick(record)}>
                      {value}
                    </AntLink>
                  );
                },
              },
              {
                title: 'Count',
                dataIndex: 'count',
                key: 'count',
                sorter: (a, b) => (a.count || 0) - (b.count || 0),
              },
            ]}
            dataSource={topNData}
            rowKey={(record) => `${record.name || ''}-${record.value || ''}-${record.count || 0}`}
            scroll={{ y: 'calc(100vh - 250px)' }}
            pagination={false}
            expandable={{
              childrenColumnName: '_children',
            }}
          />
        </div>
      ),
    },
    {
      key: 'validation',
      label: 'Validation issues',
      children: (
        <div className={styles.tabContent}>
          <Table
            columns={[
              {
                title: 'Name',
                dataIndex: 'name',
                key: 'name',
                width: 300,
                render: (value: string, record: ValidationIssue) => (
                  <AntLink className={styles.cellLink} onClick={() => handleValidationRowClick(record)}>
                    {value}
                  </AntLink>
                ),
              },
              {
                title: 'Count',
                dataIndex: 'count',
                key: 'count',
                sorter: (a, b) => a.count - b.count,
              },
            ]}
            dataSource={validationData}
            rowKey="name"
            scroll={{ y: 'calc(100vh - 250px)' }}
            pagination={false}
          />
        </div>
      ),
    },
  ];

  return (
    <div className={styles.container}>
      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} type="card" />

      <Modal
        title="Compare Data"
        open={compareModalOpen}
        onCancel={() => setCompareModalOpen(false)}
        width="95vw"
        footer={null}
        style={{ top: 20 }}
      >
        <div className={styles.compareModal}>
          <div className={styles.iframeColumn}>
            <iframe ref={iframeRef} srcDoc={htmlContent} className={styles.iframe} sandbox="allow-same-origin" />
          </div>
          <div className={styles.dataColumn}>
            {selectedRowData && (
              <table className={styles.detailTable}>
                <tbody>
                  {Object.entries(selectedRowData)
                    .filter(([key]) => key !== '_rowId') // Exclude internal fields
                    .map(([key, value]) => {
                      const cellStyle: React.CSSProperties = {};

                      // Apply validation error highlighting
                      if (selectedRowData._validationErrors && Array.isArray(selectedRowData._validationErrors)) {
                        const prefix = key + '/';
                        const hasError = selectedRowData._validationErrors.some((err: any) => {
                          const errStr = typeof err === 'string' ? err : err?.element;
                          return errStr && errStr.startsWith(prefix);
                        });
                        if (hasError) {
                          cellStyle.backgroundColor = 'orange';
                        }
                      }

                      // Apply dupe/filtered highlighting
                      if (selectedRowData._dupe || selectedRowData._filtered) {
                        cellStyle.backgroundColor = '#2f4f4f';
                        cellStyle.color = 'white';
                      }

                      // Render the value based on the column rendering logic
                      const renderValue = () => {
                        // For _-prefixed fields, just show the rendered value using the same formatter
                        if (key[0] === '_') {
                          return renderCellValue(key, value, snapshot?.crawlRunId);
                        }

                        // For other fields, show rendered value + JSON + raw data
                        const renderedValue = renderCellValue(key, value, snapshot?.crawlRunId);

                        return (
                          <>
                            <div>{renderedValue}</div>
                            <hr />
                            <h6>JSON</h6>
                            <pre style={{ color: 'inherit', whiteSpace: 'pre-wrap' }}>{JSON.stringify(value, null, 2)}</pre>
                            {selectedRowData._raw && selectedRowData._raw[key] && (
                              <>
                                <hr />
                                <h6>Raw data</h6>
                                <div>
                                  {Array.isArray(selectedRowData._raw[key])
                                    ? selectedRowData._raw[key].map((raw: any, idx: number) => (
                                        <div key={idx}>
                                          <pre style={{ whiteSpace: 'pre-wrap' }}>
                                            {typeof raw === 'object' ? JSON.stringify(raw, null, 2) : String(raw)}
                                          </pre>
                                          {idx < selectedRowData._raw[key].length - 1 && <hr />}
                                        </div>
                                      ))
                                    : String(selectedRowData._raw[key])}
                                </div>
                              </>
                            )}
                          </>
                        );
                      };

                      return (
                        <tr key={key} style={cellStyle}>
                          <th>{key}</th>
                          <td>{renderValue()}</td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
}

export default SnapshotDrilldown;
